import React, { useState, useEffect } from 'react';
import { userApi } from '../../api';
import { PageHeader, Table, Pagination, Modal, FormItem, message, confirm, formatDate } from '../../components/common';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [filters, setFilters] = useState({ role: '', status: '', keyword: '' });
  const [sort, setSort] = useState({ field: 'created_at', order: 'desc' });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '', password: '', roles: ['sales'], real_name: '', email: '', phone: '', status: 1
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters,
        sort_by: sort.field,
        order: sort.order
      };
      Object.keys(params).forEach(k => !params[k] && params[k] !== 0 && delete params[k]);
      const res = await userApi.getList(params);
      setUsers(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [pagination.page, pagination.per_page, sort]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const handleSort = (field, order) => {
    setSort({ field, order });
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', roles: ['sales'], real_name: '', email: '', phone: '', status: 1 });
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Ensure roles is an array (backward compatibility)
    let userRoles = user.roles || (user.role ? user.role.split(',') : []);
    setFormData({ ...user, roles: userRoles, password: '' });
    setModalVisible(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`确定删除用户 ${user.username} 吗？`)) return;
    try {
      await userApi.delete(user.id);
      message.success('删除成功');
      fetchUsers();
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await userApi.update(editingUser.id, formData);
        message.success('更新成功');
      } else {
        await userApi.create(formData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (err) {
      message.error(err.message);
    }
  };

  const roleMap = { admin: '管理员', developer: '开发者', sales: '销售' };

  const columns = [
    { key: 'id', title: 'ID', width: '60px', sorter: true },
    { key: 'username', title: '用户名', sorter: true },
    { key: 'real_name', title: '真实姓名', sorter: true },
    {
      key: 'role', title: '角色', render: (_, row) => {
        const roles = row.roles || (row.role ? row.role.split(',') : []);
        return roles.map(r => roleMap[r] || r).join(', ');
      }
    },
    { key: 'email', title: '邮箱', sorter: true },
    {
      key: 'status', title: '状态', render: (v) => (
        <span className={`tag ${v === 1 ? 'tag-success' : 'tag-error'}`}>{v === 1 ? '启用' : '禁用'}</span>
      ), sorter: true
    },
    { key: 'created_at', title: '创建时间', render: (v) => formatDate(v), sorter: true },
    {
      key: 'actions', title: '操作', render: (_, row) => (
        <div className="action-buttons">
          <button className="btn btn-small btn-default" onClick={() => handleEdit(row)}>编辑</button>
          <button className="btn btn-small btn-danger" onClick={() => handleDelete(row)}>删除</button>
        </div>
      )
    }
  ];

  const handleRoleChange = (role, checked) => {
    setFormData(prev => {
      const newRoles = checked
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role);
      return { ...prev, roles: newRoles };
    });
  };

  return (
    <div>
      <PageHeader title="用户管理" extra={<button className="btn btn-primary" onClick={handleCreate}>新建用户</button>} />

      <div className="search-bar">
        <div className="form-item">
          <input placeholder="搜索用户名/姓名" value={filters.keyword} onChange={e => setFilters({ ...filters, keyword: e.target.value })} />
        </div>
        <div className="form-item">
          <select value={filters.role} onChange={e => setFilters({ ...filters, role: e.target.value })}>
            <option value="">全部角色</option>
            <option value="admin">管理员</option>
            <option value="developer">开发者</option>
            <option value="sales">销售</option>
          </select>
        </div>
        <div className="form-item">
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
            <option value="">全部状态</option>
            <option value="1">启用</option>
            <option value="0">禁用</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={handleSearch}>搜索</button>
      </div>

      <Table
        columns={columns}
        data={users}
        loading={loading}
        sort={sort}
        onSort={handleSort}
      />
      <Pagination current={pagination.page} total={pagination.total} pageSize={pagination.per_page}
        onChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(size) => setPagination(prev => ({ ...prev, per_page: size, page: 1 }))} />

      <Modal visible={modalVisible} title={editingUser ? '编辑用户' : '新建用户'} onClose={() => setModalVisible(false)}
        footer={<><button className="btn btn-default" onClick={() => setModalVisible(false)}>取消</button>
          <button className="btn btn-primary" onClick={handleSubmit}>确定</button></>}>
        <FormItem label="用户名" required>
          <input value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })} disabled={!!editingUser} />
        </FormItem>
        <FormItem label={editingUser ? '新密码（留空不修改）' : '密码'} required={!editingUser}>
          <input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} />
        </FormItem>
        <FormItem label="角色" required>
          <div style={{ display: 'flex', gap: '10px' }}>
            {Object.keys(roleMap).map(key => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.roles.includes(key)}
                  onChange={e => handleRoleChange(key, e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                {roleMap[key]}
              </label>
            ))}
          </div>
        </FormItem>
        <FormItem label="真实姓名">
          <input value={formData.real_name || ''} onChange={e => setFormData({ ...formData, real_name: e.target.value })} />
        </FormItem>
        <FormItem label="邮箱">
          <input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} />
        </FormItem>
        <FormItem label="手机号">
          <input value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
        </FormItem>
        <FormItem label="状态">
          <select value={formData.status} onChange={e => setFormData({ ...formData, status: parseInt(e.target.value) })}>
            <option value={1}>启用</option>
            <option value={0}>禁用</option>
          </select>
        </FormItem>
      </Modal>
    </div>
  );
};

export default AdminUsers;
